// US Equity master admin view. Two tabs:
//
//   - "Listings" — daily us_equity.refresh cron output (NASDAQ Trader
//     symbol dump). Stats card, search, paginated table, manual refresh.
//   - "Snapshots" — Phase 4 / B-2 overseas.snapshot per-ticker price +
//     TA history. Stats card, latest rows table, manual snapshot trigger.
//
// Operator workflow:
//   1. Enable us_equity.enabled, hit "지금 갱신" → confirm ~10k symbols.
//   2. Configure KIS overseas API access (paid feature on some accounts).
//   3. Add tickers to watchlist OR generate US suggestions.
//   4. Enable overseas.enabled, hit "지금 스냅샷" → confirm rows in
//      Snapshots tab with non-null price + RSI/SMA columns.
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type OverseasSnapshot, type USEquityListing } from '@/api/client'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  Select,
} from '@/admin-design/_common'
import { formatRelativeKST, formatTime } from '@/lib/utils'

type MarketFilter = 'all' | 'NASDAQ' | 'NYSE' | 'AMEX' | 'BATS' | 'IEX' | 'OTC'
type Tab = 'listings' | 'snapshots'

export default function USEquity() {
  const qc = useQueryClient()
  const [tab, setTab] = useState<Tab>('listings')
  const [searchInput, setSearchInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [market, setMarket] = useState<MarketFilter>('all')

  const stats = useQuery({
    queryKey: ['us-equity', 'stats'],
    queryFn: () => api.usEquityStats(),
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  })

  const list = useQuery({
    queryKey: ['us-equity', 'list', searchTerm, market],
    queryFn: () =>
      api.usEquityListings({
        q: searchTerm || undefined,
        market: market === 'all' ? undefined : market,
        limit: 100,
      }),
    refetchOnWindowFocus: false,
    enabled: tab === 'listings',
  })

  const refreshMut = useMutation({
    mutationFn: () => api.usEquityRefresh(),
    onSettled: () => {
      // The refresh runs async on the server; poll stats a few seconds
      // later to give it a chance to write rows.
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: ['us-equity'] })
      }, 5_000)
    },
  })

  const stale = stats.data?.stale_hours_ago !== undefined &&
    stats.data.stale_hours_ago > 36

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">US Equity</h1>
          <p className="text-sm text-muted-foreground">
            NASDAQ Trader 공식 심볼 dump + KIS 해외주식 가격 스냅샷.
          </p>
        </div>
        {tab === 'listings' && (
          <Button
            size="sm"
            variant="primary"
            onClick={() => refreshMut.mutate()}
            disabled={refreshMut.isPending}
          >
            {refreshMut.isPending ? '갱신 중…' : '지금 갱신'}
          </Button>
        )}
      </header>

      <div className="flex gap-2 border-b border-border">
        <TabButton active={tab === 'listings'} onClick={() => setTab('listings')}>
          Listings
        </TabButton>
        <TabButton active={tab === 'snapshots'} onClick={() => setTab('snapshots')}>
          Snapshots (KIS prices)
        </TabButton>
      </div>

      {tab === 'snapshots' ? (
        <OverseasSnapshotsPanel />
      ) : (
        <ListingsPanel
          loading={stats.isPending}
          total={stats.data?.total ?? 0}
          breakdown={stats.data?.breakdown ?? []}
          lastRefreshedAt={stats.data?.last_refreshed_at}
          stale={stale}
          refreshMut={refreshMut}
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          market={market}
          setMarket={setMarket}
          list={list}
        />
      )}
    </div>
  )
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ' +
        (active
          ? 'border-primary text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground')
      }
    >
      {children}
    </button>
  )
}

type ListingsPanelProps = {
  loading: boolean
  total: number
  breakdown: { market: string; count: number }[]
  lastRefreshedAt?: number
  stale: boolean
  refreshMut: ReturnType<
    typeof useMutation<{ started_at: number; status: string }, Error, void>
  >
  searchInput: string
  setSearchInput: (s: string) => void
  searchTerm: string
  setSearchTerm: (s: string) => void
  market: MarketFilter
  setMarket: (m: MarketFilter) => void
  list: ReturnType<typeof useQuery<{ listings: USEquityListing[] }>>
}

function ListingsPanel({
  loading,
  total,
  breakdown,
  lastRefreshedAt,
  stale,
  refreshMut,
  searchInput,
  setSearchInput,
  searchTerm,
  setSearchTerm,
  market,
  setMarket,
  list,
}: ListingsPanelProps) {
  return (
    <div className="space-y-4">
      {refreshMut.isError && (
        <Card className="border-destructive-soft bg-destructive-soft">
          <Card.Body className="text-sm text-destructive-soft-foreground">
            갱신 트리거 실패: {String(refreshMut.error)}
          </Card.Body>
        </Card>
      )}
      {refreshMut.isSuccess && refreshMut.data && (
        <Card className="border-info-soft bg-info-soft">
          <Card.Body className="text-sm text-info-soft-foreground">
            갱신 시작됨 — 약 30초 후 통계가 갱신됩니다.
          </Card.Body>
        </Card>
      )}

      <StatsCard
        loading={loading}
        total={total}
        breakdown={breakdown}
        lastRefreshedAt={lastRefreshedAt}
        stale={stale}
      />

      <Card>
        <Card.Body className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">검색</label>
            <div className="flex items-center gap-2">
              <Input
                type="text"
                placeholder="symbol 또는 회사명 (예: AAPL, apple)"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') setSearchTerm(searchInput.trim())
                }}
                className="w-72"
              />
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setSearchTerm(searchInput.trim())}
              >
                검색
              </Button>
              {searchTerm && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setSearchInput('')
                    setSearchTerm('')
                  }}
                >
                  초기화
                </Button>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Market</label>
            <Select
              value={market}
              onChange={(e) => setMarket(e.target.value as MarketFilter)}
              className="w-36"
              options={[
                { value: 'all', label: '전체' },
                { value: 'NASDAQ', label: 'NASDAQ' },
                { value: 'NYSE', label: 'NYSE' },
                { value: 'AMEX', label: 'AMEX' },
                { value: 'BATS', label: 'BATS' },
                { value: 'IEX', label: 'IEX' },
                { value: 'OTC', label: 'OTC' },
              ]}
            />
          </div>
        </Card.Body>
      </Card>

      {list.isPending && (
        <Card>
          <Card.Body>
            <p className="text-sm text-muted-foreground">로딩 중…</p>
          </Card.Body>
        </Card>
      )}
      {list.error && (
        <Card className="border-destructive-soft bg-destructive-soft">
          <Card.Body className="text-sm text-destructive-soft-foreground">
            로드 실패: {String(list.error)}
          </Card.Body>
        </Card>
      )}

      {list.data && list.data.listings.length === 0 && (
        <Card>
          <Card.Body>
            <EmptyState
              title="검색 결과가 없습니다"
              description={
                total
                  ? '다른 키워드로 시도하거나 market 필터를 변경하세요.'
                  : '아직 마스터에 데이터가 없습니다. "지금 갱신" 버튼으로 첫 페치를 실행하세요.'
              }
            />
          </Card.Body>
        </Card>
      )}

      {list.data && list.data.listings.length > 0 && (
        <Card>
          <Card.Body className="overflow-x-auto p-0">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/40 text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Symbol</th>
                  <th className="px-3 py-2 text-left font-medium">Name</th>
                  <th className="px-3 py-2 text-left font-medium">Market</th>
                  <th className="px-3 py-2 text-left font-medium">ETF</th>
                  <th className="px-3 py-2 text-right font-medium">갱신</th>
                </tr>
              </thead>
              <tbody>
                {list.data.listings.map((l) => (
                  <ListingRow key={l.symbol} listing={l} />
                ))}
              </tbody>
            </table>
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

function StatsCard({
  loading,
  total,
  breakdown,
  lastRefreshedAt,
  stale,
}: {
  loading: boolean
  total: number
  breakdown: { market: string; count: number }[]
  lastRefreshedAt?: number
  stale: boolean
}) {
  return (
    <Card>
      <Card.Header>
        <Card.Title>통계</Card.Title>
      </Card.Header>
      <Card.Body className="flex flex-wrap items-start gap-6">
        <div>
          <div className="text-xs text-muted-foreground">총 종목 수</div>
          <div className="text-2xl font-semibold">
            {loading ? '…' : total.toLocaleString()}
          </div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">마지막 갱신</div>
          <div
            className="text-base"
            title={lastRefreshedAt ? formatTime(lastRefreshedAt) : undefined}
          >
            {loading
              ? '…'
              : lastRefreshedAt
                ? formatRelativeKST(lastRefreshedAt)
                : '없음'}
          </div>
          {stale && (
            <Badge variant="warning" shape="pill" className="mt-1">
              36시간 경과
            </Badge>
          )}
        </div>
        <div className="flex-1 min-w-[280px]">
          <div className="mb-1 text-xs text-muted-foreground">시장별</div>
          <div className="flex flex-wrap gap-2">
            {breakdown.length === 0 ? (
              <span className="text-sm text-muted-foreground">데이터 없음</span>
            ) : (
              breakdown.map((row) => (
                <Badge
                  key={row.market}
                  variant={marketBadgeVariant(row.market)}
                  shape="square"
                  title={`${row.market}: ${row.count.toLocaleString()}`}
                >
                  {row.market} {row.count.toLocaleString()}
                </Badge>
              ))
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}

function ListingRow({ listing }: { listing: USEquityListing }) {
  return (
    <tr className="border-b border-border last:border-0 hover:bg-muted/30">
      <td className="px-3 py-2 font-mono text-xs">{listing.symbol}</td>
      <td className="px-3 py-2">{listing.name}</td>
      <td className="px-3 py-2">
        <Badge
          variant={marketBadgeVariant(listing.market)}
          shape="pill"
        >
          {listing.market}
        </Badge>
      </td>
      <td className="px-3 py-2">
        {listing.etf ? (
          <Badge variant="info" shape="square">ETF</Badge>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </td>
      <td
        className="px-3 py-2 text-right text-xs text-muted-foreground"
        title={formatTime(listing.last_refreshed_at)}
      >
        {formatRelativeKST(listing.last_refreshed_at)}
      </td>
    </tr>
  )
}

function marketBadgeVariant(
  market: string,
): 'success' | 'info' | 'warning' | 'muted' {
  switch (market) {
    case 'NASDAQ':
      return 'success'
    case 'NYSE':
      return 'info'
    case 'AMEX':
      return 'warning'
    default:
      return 'muted'
  }
}

// ----- Overseas snapshots panel ---------------------------------------------

function OverseasSnapshotsPanel() {
  const qc = useQueryClient()
  const [market, setMarket] = useState<MarketFilter>('all')

  const stats = useQuery({
    queryKey: ['overseas', 'stats'],
    queryFn: () => api.overseasStats(),
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  })

  const list = useQuery({
    queryKey: ['overseas', 'snapshots', market],
    queryFn: () =>
      api.overseasSnapshotsLatest({
        market: market === 'all' ? undefined : market,
        limit: 100,
      }),
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  })

  const snapshotMut = useMutation({
    mutationFn: () => api.overseasSnapshot(),
    onSettled: () => {
      // Tick takes ~30s for ~50 tickers; poll a bit later.
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: ['overseas'] })
      }, 5_000)
    },
  })

  const stale = stats.data?.stale_hours_ago !== undefined &&
    stats.data.stale_hours_ago > 6

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          KIS 해외주식 API 로 watchlist + 최근 추천 종목의 spot 가격 + TA
          지표를 매 30분 (US 시간 기준) 스냅샷합니다. overseas.enabled=ON
          후 동작.
        </p>
        <Button
          size="sm"
          variant="primary"
          onClick={() => snapshotMut.mutate()}
          disabled={snapshotMut.isPending}
        >
          {snapshotMut.isPending ? '스냅샷 중…' : '지금 스냅샷'}
        </Button>
      </div>

      {snapshotMut.isError && (
        <Card className="border-destructive-soft bg-destructive-soft">
          <Card.Body className="text-sm text-destructive-soft-foreground">
            스냅샷 트리거 실패: {String(snapshotMut.error)}
          </Card.Body>
        </Card>
      )}
      {snapshotMut.isSuccess && snapshotMut.data && (
        <Card className="border-info-soft bg-info-soft">
          <Card.Body className="text-sm text-info-soft-foreground">
            스냅샷 시작됨 — KIS API 호출이 완료되면 ~30초 내 표가 갱신됩니다.
          </Card.Body>
        </Card>
      )}

      <Card>
        <Card.Header>
          <Card.Title>스냅샷 통계</Card.Title>
        </Card.Header>
        <Card.Body className="flex flex-wrap items-start gap-6">
          <div>
            <div className="text-xs text-muted-foreground">총 행 수</div>
            <div className="text-2xl font-semibold">
              {stats.isPending
                ? '…'
                : (stats.data?.total_rows ?? 0).toLocaleString()}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">고유 ticker</div>
            <div className="text-2xl font-semibold">
              {stats.isPending
                ? '…'
                : (stats.data?.unique_tickers ?? 0).toLocaleString()}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">마지막 캡쳐</div>
            <div
              className="text-base"
              title={
                stats.data?.last_captured_at
                  ? formatTime(stats.data.last_captured_at)
                  : undefined
              }
            >
              {stats.isPending
                ? '…'
                : stats.data?.last_captured_at
                  ? formatRelativeKST(stats.data.last_captured_at)
                  : '없음'}
            </div>
            {stale && (
              <Badge variant="warning" shape="pill" className="mt-1">
                6시간 경과
              </Badge>
            )}
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Market</label>
            <Select
              value={market}
              onChange={(e) => setMarket(e.target.value as MarketFilter)}
              className="w-36"
              options={[
                { value: 'all', label: '전체' },
                { value: 'NASDAQ', label: 'NASDAQ' },
                { value: 'NYSE', label: 'NYSE' },
                { value: 'AMEX', label: 'AMEX' },
                { value: 'BATS', label: 'BATS' },
              ]}
            />
          </div>
        </Card.Body>
      </Card>

      {list.isPending && (
        <Card>
          <Card.Body>
            <p className="text-sm text-muted-foreground">로딩 중…</p>
          </Card.Body>
        </Card>
      )}
      {list.error && (
        <Card className="border-destructive-soft bg-destructive-soft">
          <Card.Body className="text-sm text-destructive-soft-foreground">
            로드 실패: {String(list.error)}
          </Card.Body>
        </Card>
      )}

      {list.data && list.data.snapshots.length === 0 && (
        <Card>
          <Card.Body>
            <EmptyState
              title="스냅샷이 없습니다"
              description={
                stats.data?.total_rows
                  ? '선택한 market 에 데이터가 없습니다. 다른 market 을 선택하거나 universe 를 점검하세요.'
                  : 'overseas.enabled=ON + KIS 해외주식 API 권한 + watchlist/추천 종목이 있어야 첫 스냅샷이 동작합니다.'
              }
            />
          </Card.Body>
        </Card>
      )}

      {list.data && list.data.snapshots.length > 0 && (
        <Card>
          <Card.Body className="overflow-x-auto p-0">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/40 text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Ticker</th>
                  <th className="px-3 py-2 text-left font-medium">Market</th>
                  <th className="px-3 py-2 text-right font-medium">Price</th>
                  <th className="px-3 py-2 text-right font-medium">1d %</th>
                  <th className="px-3 py-2 text-right font-medium">5d %</th>
                  <th className="px-3 py-2 text-right font-medium">RSI14</th>
                  <th className="px-3 py-2 text-right font-medium">SMA20</th>
                  <th className="px-3 py-2 text-right font-medium">Vol×</th>
                  <th className="px-3 py-2 text-right font-medium">캡쳐</th>
                </tr>
              </thead>
              <tbody>
                {list.data.snapshots.map((sn) => (
                  <SnapshotRow
                    key={`${sn.ticker}-${sn.captured_at}`}
                    snapshot={sn}
                  />
                ))}
              </tbody>
            </table>
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

function SnapshotRow({ snapshot: sn }: { snapshot: OverseasSnapshot }) {
  return (
    <tr className="border-b border-border last:border-0 hover:bg-muted/30">
      <td className="px-3 py-2 font-mono text-xs">{sn.ticker}</td>
      <td className="px-3 py-2">
        <Badge variant={marketBadgeVariant(sn.market)} shape="pill">
          {sn.market}
        </Badge>
      </td>
      <td className="px-3 py-2 text-right font-mono">
        {sn.last_price > 0 ? sn.last_price.toFixed(2) : '—'}
      </td>
      <td className="px-3 py-2 text-right font-mono">
        <ChangeCell value={sn.change_pct_1d} />
      </td>
      <td className="px-3 py-2 text-right font-mono">
        <ChangeCell value={sn.change_pct_5d} />
      </td>
      <td className="px-3 py-2 text-right font-mono">
        {sn.rsi_14 !== undefined ? sn.rsi_14.toFixed(1) : '—'}
      </td>
      <td className="px-3 py-2 text-right font-mono">
        {sn.sma_20 !== undefined ? sn.sma_20.toFixed(2) : '—'}
      </td>
      <td className="px-3 py-2 text-right font-mono">
        {sn.volume_ratio !== undefined ? sn.volume_ratio.toFixed(2) : '—'}
      </td>
      <td
        className="px-3 py-2 text-right text-xs text-muted-foreground"
        title={formatTime(sn.captured_at)}
      >
        {formatRelativeKST(sn.captured_at)}
      </td>
    </tr>
  )
}

function ChangeCell({ value }: { value?: number }) {
  if (value === undefined) return <span className="text-muted-foreground">—</span>
  if (value > 0) return <span className="text-success">+{value.toFixed(2)}%</span>
  if (value < 0) return <span className="text-destructive">{value.toFixed(2)}%</span>
  return <span>{value.toFixed(2)}%</span>
}
